/*
  Copyright Epic Games, Inc. All Rights Reserved.
*/

import router from "next/navigation";
import React, { useContext, PropsWithChildren, ComponentType } from "react";
import type Lottie from "lottie-react";
import type { UrlObject } from "url";

import { CosmosImageReactElementType, DefaultImage } from "./DefaultImage";
import { CosmosLinkReactElementType, DefaultLink } from "./DefaultLink";
import type { LoadingProps } from "./DefaultLoading";
import type { PaddingBlockVariants } from "@components/utils/section.utils";

interface Pushable {
	push: (path: string | UrlObject) => void;
}

/**
 * Function to generate locale-aware paths.
 * @param path - The path without locale prefix (e.g., "/about")
 * @param locale - The current locale (e.g., "de")
 * @returns The localized path based on site configuration
 */
export type GetLocalizedPathFn = (path: string, locale: string) => string;

export interface CosmosComponentContextProps extends PropsWithChildren {
	Image: CosmosImageReactElementType;
	Link: CosmosLinkReactElementType;
	LoadingComponent?: ComponentType<LoadingProps>;
	Lottie?: typeof Lottie;
	feedBrand:
		"unreal-engine" | "eos" | "deveg" | "twinmotion" | "kws" | "mh" | "rs" | "eg" | "ue-editor" | "quixel" | "fab"; // from wf-services-src feed-service Brand
	feedRootPath: string;
	hygraphMediaDomain: string;
	router: Pushable | typeof router;
	enableAuth: boolean;
	locales: string[];
	paddingBlockVariant?: PaddingBlockVariants;
	maxWidth?: string;
	disableAnimations?: boolean;
	/**
	 * Optional function to generate locale-aware paths.
	 * If not provided, defaults to always prefixing with locale (e.g., `/${locale}${path}`).
	 * Websites using "as-needed" locale prefix mode should provide this function.
	 */
	getLocalizedPath?: GetLocalizedPathFn;
	siteBasePath?: string;
}

/**
 * Default locale path generation - always prefix with locale.
 * This is the default behavior for most websites in the monorepo.
 * Exported for use in non-React contexts (utilities, etc.)
 */
export const defaultGetLocalizedPath: GetLocalizedPathFn = (path, locale) => {
	const normalizedPath = path.startsWith("/") ? path : `/${path}`;
	return `/${locale}${normalizedPath}`;
};

export const CosmosComponentContext = React.createContext<CosmosComponentContextProps>({
	Image: DefaultImage,
	Link: DefaultLink,
	LoadingComponent: undefined,
	Lottie: undefined,
	feedBrand: "unreal-engine",
	feedRootPath: "/feed",
	hygraphMediaDomain: "cms-assets.unrealengine.com",
	router: {
		push: () => {
			console.log("CosmosComponentContext.router() default push handler ");
		},
	},
	enableAuth: true,
	locales: ["en-US"],
	paddingBlockVariant: "default",
	maxWidth: "1440px",
	disableAnimations: false,
	getLocalizedPath: defaultGetLocalizedPath,
	siteBasePath: "",
});

export const useCosmosComponentContext = (): CosmosComponentContextProps => {
	return useContext<CosmosComponentContextProps>(CosmosComponentContext);
};
